import {useMutation, useQuery} from '@apollo/client'
import {Alert, Button, FloatingLabel, Form, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {GET_ALL_REASON_LOSS} from '../../../gql/queries/reasonLossQuery'
import {IResAllReasons} from '../../../types/reasonLoss'
import {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {DEAD_LEAD} from '../../../gql/mutations/leadMutations'
import {parseId} from '../../../helpers'
import {IResDeadLead} from '../../../types/leads'
import {useLocation, useNavigate} from 'react-router-dom'

interface IModalDeadProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  info: {
    id: string
    name: string
  }
}

const space = ' '

const dM = defineMessages({
  confirm: {id: 'leads.dead.confirm', defaultMessage: 'Are you sure you want to close this lead? If so, please select the reason for closing the lead below'},
  toastRemoved: {id: 'leads.dead.toast.removed', defaultMessage: 'Lead has been removed'},
  somethingWrong: {id: 'leads.dead.somethingWrong', defaultMessage: 'Something wrong!'},
})

export const ModalDeadLead = ({isOpen, onClose, refetch, info}: IModalDeadProps) => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})

  const [gqlDeadLead, {data, loading, error}] = useMutation<IResDeadLead>(DEAD_LEAD)
  const {
    data: dataReason,
    loading: loadingReason,
    error: errorReason,
  } = useQuery<IResAllReasons>(GET_ALL_REASON_LOSS, {fetchPolicy: 'no-cache'})

  const navigate = useNavigate()
  const location = useLocation()

  const [reasonLeadLossId, setReasonLeadLossId] = useState('')

  useEffect(() => {
    if (errorReason) toast.error(errMsg(errorReason.message))
  }, [errorReason])

  useEffect(() => {
    if (error) toast.error(errMsg(error.message))
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.declineLeadMutation.success) {
      toast.success(intl.formatMessage(dM.toastRemoved))
      refetch()
      if (location.pathname.startsWith('/lead/')) {
        navigate('/leads')
      }
      onClose()
    } else if (data.declineLeadMutation.errors && data.declineLeadMutation.errors.length > 0) {
      toast.error(errMsg(data.declineLeadMutation.errors?.[0]?.message || intl.formatMessage(dM.somethingWrong)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <Modal show={isOpen} onHide={onClose} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage id='leads.detail.dead' defaultMessage='Dead' /> <span className='fw-bold text-uppercase'>{info.name}</span>{space}
          {loadingReason && <Spinner animation='border' size='sm' />}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant='danger' className='fs-3 mb-5 d-flex align-items-center gap-1'>
          <span>
            <i className='bi bi-exclamation-circle fs-1' style={{color: 'inherit'}} />
          </span>
          {intl.formatMessage(dM.confirm)}
        </Alert>
        <div>
          <FloatingLabel controlId='floatingSelect' label={intl.formatMessage({id: 'leads.list.summary.reasonForLoss', defaultMessage: 'Reason for loss'})}>
            <Form.Select
              aria-label={intl.formatMessage({id: 'leads.list.summary.reasonForLoss', defaultMessage: 'Reason for loss'})}
              disabled={loadingReason || !dataReason}
              value={reasonLeadLossId}
              onChange={(e) => setReasonLeadLossId(e.target.value)}
            >
              <option value=''>{intl.formatMessage({id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'})}</option>
              {dataReason?.allReasonLeadLoss?.edges?.map(({node}) => (
                <option key={node.id} value={node.id}>
                  {node.reason}
                </option>
              ))}
            </Form.Select>
          </FloatingLabel>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} variant='light'>
          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
        </Button>
        <Button
          onClick={() => {
            if (!reasonLeadLossId) return
            gqlDeadLead({
              variables: {
                leadId: parseId(info.id),
                reasonLeadLossId: parseId(reasonLeadLossId),
              },
            })
          }}
          variant='danger'
          disabled={loading}
        >
          <FormattedMessage id='leads.detail.dead' defaultMessage='Dead' />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
